import React, { useState } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import { useAuth } from '../hooks/useAuth';

const NotificationCenter = () => {
    const { user } = useAuth();
    const {
        notifications,
        loading,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        clearAllNotifications
    } = useNotifications(user);

    const [filter, setFilter] = useState('all'); // all, unread, read
    const [selectedNotifications, setSelectedNotifications] = useState(new Set());

    // Filtrar notificaciones
    const filteredNotifications = notifications.filter(notification => {
        if (filter === 'unread') return !notification.leida;
        if (filter === 'read') return notification.leida;
        return true;
    });

    // Formatear fecha
    const formatDate = (date) => {
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Obtener color por tipo de notificación
    const getTypeColor = (tipo) => {
        const colors = {
            estudio: '#4CAF50',
            progreso: '#FF9800',
            comunidad: '#2196F3',
            examen: '#F44336',
            sistema: '#9C27B0'
        };
        return colors[tipo] || '#666';
    };

    // Obtener icono por tipo
    const getTypeIcon = (tipo) => {
        const icons = {
            estudio: '📚',
            progreso: '🎯',
            comunidad: '👥',
            examen: '⏰',
            sistema: 'ℹ️'
        };
        return icons[tipo] || '📢';
    };

    // Manejar selección múltiple
    const toggleSelection = (notificationId) => {
        const newSelection = new Set(selectedNotifications);
        if (newSelection.has(notificationId)) {
            newSelection.delete(notificationId);
        } else {
            newSelection.add(notificationId);
        }
        setSelectedNotifications(newSelection);
    };

    const selectAll = () => {
        if (selectedNotifications.size === filteredNotifications.length) {
            setSelectedNotifications(new Set());
        } else {
            setSelectedNotifications(new Set(filteredNotifications.map(n => n.id)));
        }
    };

    const markSelectedAsRead = async () => {
        for (const id of selectedNotifications) {
            await markAsRead(id);
        }
        setSelectedNotifications(new Set());
    };

    const deleteSelected = async () => {
        for (const id of selectedNotifications) {
            await deleteNotification(id);
        }
        setSelectedNotifications(new Set());
    };

    if (loading) {
        return (
        <div className="notification-center-container" style={{ maxWidth: '100%', margin: 0, height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto' }}>
                <div className="loading">Cargando notificaciones...</div>
            </div>
        );
    }

    return (
        <div className="notification-center-container" style={{ maxWidth: '100%', margin: 0, height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto' }}>
            <div className="notification-center-header">
                <h1>Centro de Notificaciones</h1>
                <p>Gestiona todas tus notificaciones en un solo lugar</p>
            </div>

            <div className="notification-center-controls">
                <div className="filter-tabs">
                    <button
                        className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        Todas ({notifications.length})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
                        onClick={() => setFilter('unread')}
                    >
                        No leídas ({notifications.filter(n => !n.leida).length})
                    </button>
                    <button
                        className={`filter-tab ${filter === 'read' ? 'active' : ''}`}
                        onClick={() => setFilter('read')}
                    >
                        Leídas ({notifications.filter(n => n.leida).length})
                    </button>
                </div>

                <div className="bulk-actions">
                    {filteredNotifications.length > 0 && (
                        <>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={selectedNotifications.size === filteredNotifications.length && filteredNotifications.length > 0}
                                    onChange={selectAll}
                                />
                                Seleccionar todas
                            </label>

                            {selectedNotifications.size > 0 && (
                                <div className="selected-actions">
                                    <span>{selectedNotifications.size} seleccionadas</span>
                                    <button
                                        className="bulk-read-btn"
                                        onClick={markSelectedAsRead}
                                    >
                                        Marcar como leídas
                                    </button>
                                    <button
                                        className="bulk-delete-btn"
                                        onClick={deleteSelected}
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {notifications.length > 0 && (
                        <div className="global-actions">
                            <button
                                className="mark-all-read-btn"
                                onClick={markAllAsRead}
                            >
                                Marcar todas como leídas
                            </button>
                            <button
                                className="clear-all-btn"
                                onClick={clearAllNotifications}
                            >
                                Limpiar todas
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="notification-center-content">
                {filteredNotifications.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📭</div>
                        <h3>No hay notificaciones</h3>
                        <p>
                            {filter === 'all'
                                ? 'No tienes notificaciones en este momento.'
                                : filter === 'unread'
                                    ? 'No tienes notificaciones sin leer.'
                                    : 'No tienes notificaciones leídas.'
                            }
                        </p>
                    </div>
                ) : (
                    <div className="notification-list-full">
                        {filteredNotifications.map(notification => (
                            <div
                                key={notification.id}
                                className={`notification-card ${!notification.leida ? 'unread' : ''} ${selectedNotifications.has(notification.id) ? 'selected' : ''}`}
                            >
                                <div className="notification-select">
                                    <input
                                        type="checkbox"
                                        checked={selectedNotifications.has(notification.id)}
                                        onChange={() => toggleSelection(notification.id)}
                                    />
                                </div>

                                <div className="notification-type-indicator">
                                    <span
                                        className="type-icon"
                                        style={{ backgroundColor: getTypeColor(notification.tipo) }}
                                    >
                                        {getTypeIcon(notification.tipo)}
                                    </span>
                                </div>

                                <div className="notification-main">
                                    <div className="notification-header">
                                        <h4 className="notification-title">{notification.titulo}</h4>
                                        <span className="notification-type">{notification.tipo}</span>
                                    </div>

                                    <p className="notification-message">{notification.mensaje}</p>

                                    <div className="notification-meta">
                                        <span className="notification-date">
                                            {formatDate(notification.fechaCreacion)}
                                        </span>
                                        {!notification.leida && (
                                            <span className="unread-badge">No leída</span>
                                        )}
                                    </div>
                                </div>

                                <div className="notification-actions">
                                    {!notification.leida && (
                                        <button
                                            className="mark-read-btn"
                                            onClick={() => markAsRead(notification.id)}
                                            title="Marcar como leída"
                                        >
                                            ✓
                                        </button>
                                    )}
                                    <button
                                        className="delete-btn"
                                        onClick={() => deleteNotification(notification.id)}
                                        title="Eliminar"
                                    >
                                        🗑️
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationCenter;